import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaHome, FaThLarge, FaBullhorn, FaBookOpen, FaShieldAlt } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import NotificationBell from "./NotificationBell";
import ProfileHeader from "../ProfileHeader";
import logo_eagle from "../assets/img/logo_eagle.png";
import "./components.css";

/**
 * Icon-only nav — no text labels, but every destination is still reachable.
 * The label lives in aria-label (for screen readers) and title (for a hover tooltip),
 * so dropping the visible words costs nothing in accessibility.
 *
 * These SCROLL to a section of Home rather than routing to a standalone page. Routing
 * meant clicking "Updates" replaced the whole screen with just that list, and the only
 * way back was the Home icon. The dedicated list pages still exist and are still linked
 * from each section's "View all" — this nav is for moving around the page.
 *
 * `id` is the element id on Home; "top" is the special case that scrolls to the start.
 */
const NAV = [
    // `always: true` items show for everyone (Home, and the dApps launcher which isn't
    // governed by section visibility). The rest appear only once the viewer is known to
    // be allowed to see that section.
    { id: "top", label: "Home", icon: <FaHome />, always: true },
    { id: "apps", label: "dApps", icon: <FaThLarge />, always: true },
    { id: "updates", label: "Updates", icon: <FaBullhorn /> },
    { id: "blog", label: "Blog", icon: <FaBookOpen /> },
    { id: "policies", label: "Policies", icon: <FaShieldAlt /> },
];

/** Respect a reduced-motion preference — smooth scrolling is exactly what it opts out of. */
const scrollBehavior = () =>
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";

/** Scroll offset comes from scroll-margin-top on the sections, so the sticky header
 *  never covers the heading we just scrolled to. */
const scrollToSection = (id) => {
    if (id === "top") {
        window.scrollTo({ top: 0, behavior: scrollBehavior() });
        return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: scrollBehavior(), block: "start" });
};

/**
 * The one site header — sticky, full-bleed, and rendered from REAL auth state.
 *
 * Brand sits hard against the left edge, actions hard against the right. There are no
 * nav links: navigation lives in the Home sections' "View all" links and the footer.
 *
 * VISITOR  → Login button. No bell, no profile.
 * EMPLOYEE → notification bell + profile menu. No Login button.
 *
 * `isAuthenticated` comes from the AuthProvider, which derives it server-side from the
 * httpOnly JWT (/api/employee/me) — not from anything the client can flip. The
 * employee-only widgets are NOT rendered-and-hidden; they are not mounted at all, so a
 * visitor never fetches employee data.
 */
const Header = () => {
    const { isAuthenticated, employee, setEmployee, loading, isSectionVisible } = useAuth();
    const [scrolled, setScrolled] = useState(false);
    const [progress, setProgress] = useState(0);

    const navigate = useNavigate();
    const location = useLocation();

    // Condense the bar and drive the reading-progress rail. Passive listener + rAF so
    // this never blocks scrolling.
    useEffect(() => {
        let frame = 0;

        const onScroll = () => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
                const y = window.scrollY;
                const max = document.documentElement.scrollHeight - window.innerHeight;
                setScrolled(y > 8);
                setProgress(max > 0 ? Math.min(1, y / max) : 0);
                frame = 0;
            });
        };

        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll);
            if (frame) cancelAnimationFrame(frame);
        };
    }, []);

    const onHome = location.pathname === "/";

    /**
     * Which icon lights up. On Home that's whichever section the reader is looking at —
     * a scroll spy, since the URL no longer changes as they move down the page. Off Home
     * nothing is highlighted, because none of these sections are on screen.
     *
     * The rootMargin band ignores the top ~45% and bottom ~50% of the viewport, so a
     * section counts as "current" only once it reaches the middle of the screen rather
     * than the instant one pixel of it appears.
     */
    const [activeSection, setActiveSection] = useState("top");

    useEffect(() => {
        if (!onHome) return;

        // No IntersectionObserver (jsdom, ancient browsers) → no scroll spy. The nav still
        // scrolls correctly; it just doesn't light up. Never let the highlight break the nav.
        if (typeof IntersectionObserver === "undefined") return;

        const sections = NAV.filter((n) => n.id !== "top")
            .map((n) => document.getElementById(n.id))
            .filter(Boolean);
        if (!sections.length) return;

        const io = new IntersectionObserver(
            (entries) => {
                const visible = entries.filter((e) => e.isIntersecting);
                if (visible.length) setActiveSection(visible[0].target.id);
            },
            { rootMargin: "-45% 0px -50% 0px" }
        );
        sections.forEach((el) => io.observe(el));

        // Above the first section, nothing is "current" but Home.
        const onScroll = () => { if (window.scrollY < 200) setActiveSection("top"); };
        window.addEventListener("scroll", onScroll, { passive: true });

        return () => {
            io.disconnect();
            window.removeEventListener("scroll", onScroll);
        };
    }, [onHome]);

    /** Off Home, go Home first and scroll once it has rendered. */
    const goToSection = (id) => {
        if (onHome) {
            scrollToSection(id);
            setActiveSection(id);
        } else {
            navigate("/", { state: { scrollTo: id } });
        }
    };

    const goToLogin = () => {
        // Remember where they were — logging in returns them here, it does not send
        // them off to a separate "employee area".
        navigate("/login", { state: { from: location } });
    };

    return (
        <header className={`site-header${scrolled ? " is-scrolled" : ""}`}>
            <div className="header-inner">
                <Link to="/" className="brand" aria-label="Dolluz Corp home">
                    <span className="brand-mark">
                        <img src={logo_eagle} alt="" className="brand-logo" />
                    </span>
                    <span className="brand-text">Dolluz Corp<span className="brand-dot">.</span></span>
                </Link>

                {/* Fills the middle of the bar, which was a wide empty gap. */}
                <nav className="site-nav" aria-label="Main">
                    {/* Home always shows; the rest only once we know the viewer may see
                        them. A section closed to visitors is not listed at all — offering
                        the link would both 404 and reveal the section exists. */}
                    {NAV.filter((item) => item.always || isSectionVisible(item.id)).map((item) => {
                        const active = onHome && activeSection === item.id;
                        return (
                            <button
                                key={item.id}
                                type="button"
                                className={`nav-icon${active ? " active" : ""}`}
                                onClick={() => goToSection(item.id)}
                                aria-label={item.label}
                                title={item.label}
                                aria-current={active ? "true" : undefined}
                            >
                                {item.icon}
                            </button>
                        );
                    })}
                </nav>

                {/* Reading progress, pinned to the BOTTOM edge of the pill. Decorative,
                    so it is hidden from assistive tech. It sits between the nav and the
                    actions in source order (not last) so the header's first/last children
                    stay the brand and the actions. It is absolutely positioned, so its
                    place in the flex flow doesn't matter. */}
                <span
                    className="header-progress"
                    style={{ transform: `scaleX(${progress})` }}
                    aria-hidden="true"
                />

                <div className="header-actions">
                    {/* Render neither until auth resolves — flashing a Login button at a
                        signed-in employee is worse than a beat of nothing. */}
                    {!loading && (isAuthenticated ? (
                        <>
                            <NotificationBell />
                            <ProfileHeader loggedInEmp={employee} setLoggedInEmp={setEmployee} />
                        </>
                    ) : (
                        <button type="button" className="btn btn-primary btn-sm btn-shine" onClick={goToLogin}>
                            Login
                        </button>
                    ))}
                </div>
            </div>
        </header>
    );
};

export default Header;
